import './i18n';
import './amplify.config.js';
import './styles/commonPageStyles.scss';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Loading } from '@whistler-ui/k2/components/Loading';
import { AppMessagesContainer } from '@whistler-ui/k2/hooks';
import devtools from 'devtools-detect';
import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';

import { EntitlementAssistantDrawer } from './components/AI/EntitlementAssistantDrawer/EntitlementAssistantDrawer.js';
import { BeamerConfig } from './components/BeamerConfig';
import { EntitlementsBootstrap } from './components/EntitlementsBootstrap';
import HubSpotTracingConfig from './components/HubSpotTracingConfig/HubSpotTracingConfig.js';
import { InternalSupportBanner } from './components/InternalSupportBanner';
import { ThemeSetup } from './components/ThemeSetup';
import { SETTINGS } from './constants';
import {
  NotificationProvider,
  ReportingProvider,
  UpgradeProvider,
  WebSheetProvider,
} from './context';
import { CookieBasedIntegrationsWrapper } from './CookieBasedIntegrationsWrapper';
import { initReactScan, LazyReactQueryDevtools } from './devTools';
import Routes from './routes';
import { inAuthWidget } from './routes/Root.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

initReactScan(inAuthWidget);

if (SETTINGS.HASH_VERSION) {
  if (devtools.isOpen) {
    console.log(
      '%cHash Version: ' + import.meta.env.REACT_APP_HASH_COMMIT,
      'color: red; font-size: 20px;',
    );
  }

  window.addEventListener('devtoolschange', event => {
    if (event.detail.isOpen) {
      console.clear();
      console.log(
        '%cHash Version: ' + import.meta.env.REACT_APP_HASH_COMMIT,
        'color: red; font-size: 20px;',
      );
    }
  });
}

const renderApp = async (): Promise<void> => {
  if (import.meta.env.DEV) {
    const { startCorsSimulator } = await import('./devTools/corsSimulator');
    await startCorsSimulator();
  }

  createRoot(document.getElementById('root')).render(
    <StrictMode>
      <Suspense
        fallback={
          <div className="loading">
            <Loading />
          </div>
        }
      >
        <QueryClientProvider client={queryClient}>
          {!inAuthWidget && import.meta.env.DEV && (
            <LazyReactQueryDevtools
              initialIsOpen={false}
              buttonPosition="bottom-left"
            />
          )}
          <BrowserRouter>
            <ThemeSetup />
            <NotificationProvider>
              <EntitlementsBootstrap />
              <WebSheetProvider>
                <ReportingProvider>
                  <UpgradeProvider>
                    <HubSpotTracingConfig />
                    <EntitlementAssistantDrawer />
                    {!inAuthWidget && (
                      <>
                        <CookieBasedIntegrationsWrapper>
                          <BeamerConfig />
                        </CookieBasedIntegrationsWrapper>
                        <InternalSupportBanner />
                      </>
                    )}
                    <Routes />
                  </UpgradeProvider>
                </ReportingProvider>
                <AppMessagesContainer />
              </WebSheetProvider>
            </NotificationProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </Suspense>
    </StrictMode>,
  );
};

void renderApp();
